<template>
  <div>
    <div 
      class="m-t-5 m-b-10"
    >
      <button
        class="btn btn-success btn-sm"
        data-cy="button-add-adjustment"
        :disabled="showAddAdjustmentButton || hasModifierLimit"
        type="button"
        @click="onAddAdjustmentButtonClick"
      >
        <i class="typcn typcn-plus" />
        <span v-t="'BUTTON_ADD_NEW_ADJUSMENT'" />
      </button>
      <button
        v-if="value.length > 0"
        class="btn btn-danger btn-sm m-l-15"
        :disabled="showAddAdjustmentButton"
        type="button"
        @click="onResetAdjustments"
      >
        <span v-t="'BUTTON_RESET_ADJUSMENTS'" />
      </button>
      <button
        v-if="adjustmentsChanged"
        class="btn btn-danger btn-sm m-l-15"
        :disabled="showAddAdjustmentButton"
        type="button"
        @click="onCancelAdjustments"
      >
        <span v-t="'BUTTON_CANCEL_NEW_ADJUSMENTS'" />
      </button>
    </div>
    <div class="clearfix" />
    <div v-if="hasModifierLimit" class="bs-callout bs-callout-sm bs-callout-warning m-t-0">
      <p v-t="'MESSAGE_BID_MODIFIER_LIMIT'" />
    </div>
    <div class="draggable-table-overflow-x">
      <div
        class="bid-adjustment-stgs-list__header bid-adjustment-settings-summary"
      >
        <div
          class="bid-adjustment-settings-summary__basic-info"
        >
          <div class="draggable__dragbutton text-nowrap invisible">
            <span
              class="draggable__dragbutton-action"
            >
              <i
                class="glyphicon glyphicon-option-vertical"
                aria-hidden="true"
              />
              <i
                class="glyphicon glyphicon-option-vertical"
                aria-hidden="true"
              />
            </span>
          </div>
          <span
            v-for="header in headers"
            :key="header.property"
            :class="'bid-adjustment-settings-summary__' + header.property"
          >{{ header.name }}</span>
        </div>
      </div>
      <SortableList
        v-model="value"
        @onOrderChange="onOrderChange"
      >
        <template
          v-slot="{ tableRows }"
        >
          <div>
            <AddAdjustment
              v-if="showAddAdjustmentButton"
              v-bind="editRow ? rowOptions : adjustmentPanelProperties"
              :edit-adjustment="editRow && showAddAdjustmentButton"
              :current-row="currentRow"
              @onShowAddAdjustmentChange="onAddAdjustmentButtonClick"
              @onAddAdjustment="onAddAdjustment"
              @onEditAdjustment="onEditAdjustment"
            />

            <div
              v-if="!showAddAdjustmentButton && tableRows.length < 1"
              class="bid-adjustment-stgs-list__item bid-adjustment-settings-summary bid-adjustment-settings-no-adjustments"
            >
              {{ noRowText }}
            </div>

            <SortableItem
              v-for="row in tableRows"
              :key="row.id"
              class="bid-adjustment-stgs-list__item bid-adjustment-stgs-list__sortable sortable-item"
              :class="{'draggable-table-cursor': !showAddAdjustmentButton}"
            >
              <div
                class="bid-adjustment-settings-summary"
                :class="{'bid-adjustment-settings-summary__basic-info-row-fade-in': lastRowId == row.id}"
              >
                <SortableHandle :class="{ 'sortable-handle': !showAddAdjustmentButton }">
                  <div
                    class="bid-adjustment-settings-summary__basic-info"
                  >
                    <slot
                      name="sanitizedRow"
                      :row="row"
                    />

                    <slot
                      name="actions"
                      :row="row"
                      :showRowAdjustment="showRowAdjustment"
                    />
                  </div>
                </Sortablehandle>
              </div>
            </Sortableitem>
          </div>
        </template>
      </Sortablelist>
    </div>
  </div>
</template>

<script>
import messages from './locales';
import SortableList from './components/SortableList';
import SortableItem from './components/SortableItem';
import SortableHandle from './components/SortableHandle';
import AddAdjustment from '@/modules/dynamicInboundCalls/components/DynamicInboundCallsBidAdjustments/AddAdjustment.vue';

export default {
  name: 'DraggableTable',
  components: {
    SortableList,
    SortableItem,
    SortableHandle,
    AddAdjustment
  },
  props: {
    value: {
      type: Array,
      default() {
        return [];
      }
    },
    currentRow: {
      type: Object,
      default() {
        return {};
      }
    },
    lastRowId: {
      type: String,
      default() {
        return '';
      }
    },
    editRow: {
      type: Boolean,
      default() {
        return false;
      }
    },
    headers: {
      type: Array,
      default() {
        return [];
      }
    },
    showAddAdjustmentButton: {
      type: Boolean,
      default() {
        return false;
      }
    },
    adjustmentsChanged: {
      type: Boolean,
      default() {
        return false;
      }
    },
    adjustmentPanelProperties: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      rowOptions: this.adjustmentPanelProperties,
      bidAdjustmentsShowContents: true,
      //TODORTB make it a constant
      noRowText: 'No bid modifiers.'
    };
  },
  computed: {
    hasModifierLimit() {
      return this.value?.length >= 100;
    }
  },
  methods: {
    showRowAdjustment(row){
      this.$emit('onShowRowAdjustment', row);
      this.$emit('onAddAdjustmentButtonClick', !this.showAddAdjustmentButton);
      this.rowOptions = this.adjustmentPanelProperties;

      if(row.comparer != 'Required' && row.operation != 'Reject'){
        this.rowOptions.adjustment = {
          tagId: row.tag,
          comparisonTypeTemp: {value: row.comparer},
          comparisonValue: row.compValue,
          operationTypeTemp: {value: row.operation},
          operationValue: row.opValue,
          bulkListId: row.bulkListId,
          overrideTag: row.overrideTag,
        };

        return;
      }

      if(row.comparer == 'Required'){
        this.rowOptions.adjustment = {
          tagId: row.tag,
          comparisonTypeTemp: {value: 'REQUIRED'},
          comparisonValue: '',
          operationTypeTemp: {value: ''},
          operationValue: '',
          bulkListId: row.bulkListId,
          overrideTag: row.overrideTag,
        };

        return;
      }

      if(row.operation == 'Reject'){
        this.rowOptions.adjustment = {
          tagId: row.tag,
          comparisonTypeTemp: {value: row.comparer},
          comparisonValue: row.compValue,
          operationTypeTemp: {value: 'REJECT'},
          operationValue: 0,
          bulkListId: row.bulkListId,
          overrideTag: row.overrideTag,
        };

        return;
      }
    },
    getTagId(tag, prefix = '') {
      return prefix + tag.tagType + ':' + tag.tagName;
    },
    onShowRowAdjustment(row){
      this.$emit('onShowRowAdjustment', row);
    },
    onOrderChange(unorderedRowsAndOldAndNewIndexes) {
      this.$emit('drag', unorderedRowsAndOldAndNewIndexes);
      this.$emit('onOrderChange', unorderedRowsAndOldAndNewIndexes);
    },
    onAddAdjustmentButtonClick() {
      this.$emit('onToggleEditRow', false);
      this.$emit('onAddAdjustmentButtonClick', !this.showAddAdjustmentButton);
    },
    onAddAdjustment(newAdjustment) {
      this.$emit('onAddAdjustment', newAdjustment);
    },
    onEditAdjustment(editedAdjustment) {
      this.$emit('onEditAdjustment', editedAdjustment);
    },
    onResetAdjustments() {
      this.$emit('onResetAdjustments');
    },
    onSaveAdjustments() {
      this.$emit('onSaveAdjustments');
    },
    onCancelAdjustments() {
      this.$emit('onCancelAdjustments');
    },
    onRemoveRow(row) {
      this.$emit('onRemoveRow', row);
    },
  },
  i18n: {
    messages,
    locale: 'en'
  }
};
</script>